import { useReadOnly } from '../../Hooks/useReadOnly';
import { Permissions } from '../../Security/permissions';
import React, { useState, useEffect, useRef } from 'react';
import './ChatPage.scss';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import { ChatService } from '../../Services/ChatDataService';
import InputModal from '../../Components/InputModal/InputModal';
import { Accordion, Button, Form } from 'react-bootstrap';
import { parseMarkdownToHtml } from '../../Utils/markdownFormater';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowUp,
    faEdit,
    faTrash,
    faPlus,
    faSearch,
    faBars,
} from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';
import DynamicChart from '../../Components/DynaicChart/DynamicChart';
import DOMPurify from 'dompurify';

const ChatPageContent = ({ menu }) => {
    const readOnly = useReadOnly({ Data: Permissions.Collaboration.Chat, Documents: Permissions.Collaboration.DocuChat, DataFabric: Permissions.Collaboration.MarcbiChat, FabricCreative: Permissions.Collaboration.MarcbiCreative }[menu]);
    const { instance, accounts, inProgress } = useMsal();
    const [conversations, setConversations] = useState([]);
    const [filteredConversations, setFilteredConversations] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    const [currentConversationId, setCurrentConversationId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [userMessage, setUserMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [file, setFile] = useState(null);
    const [showNewConversationModal, setShowNewConversationModal] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [conversationToRename, setConversationToRename] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [conversationToDelete, setConversationToDelete] = useState(null);
    const [pendingMessage, setPendingMessage] = useState('');
    const [exportingMessageId, setExportingMessageId] = useState(null);

    const messagesEndRef = useRef(null);
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (inProgress === 'none' && accounts.length > 0) {
            setMessages([]);
            fetchUserConversations();
        }
    }, [accounts, instance, inProgress, menu]);

    useEffect(() => {
        setFilteredConversations(
            conversations ? conversations.filter((conv) =>
                (conv.description || '').toLowerCase().includes(searchTerm.toLowerCase())
            ) : []
        );
    }, [searchTerm, conversations]);

    const getToken = async () => {
        const request = { ...loginRequest, account: accounts[0] };
        const response = await instance.acquireTokenSilent(request);
        return response.accessToken;
    };

    const fetchUserConversations = async () => {
        const token = await getToken();
        const convs = await ChatService.getUserConversations(menu, token);
        setConversations(convs);
        setFilteredConversations(convs);
    };

    const fetchConversationHistory = async (conversationId) => {
        const token = await getToken();
        const history = await ChatService.getConversationHistory(conversationId, token);
        setMessages(history);
        scrollToBottom();
    };

    const handleCreateNewConversation = async (description) => {
        if (readOnly) return;
        const token = await getToken();
        const newConv = await ChatService.createConversation(description, menu, token);
        if (newConv) {
            fetchUserConversations();
            setCurrentConversationId(newConv.conversationId);
            setMessages([]);
        }
        if (pendingMessage) {
            setUserMessage(pendingMessage);
            setPendingMessage('');
            await handleSendMessage(new Event('submit'));
        }
        setShowNewConversationModal(false);
    };

    const normalizeAiResponse = (res) => {
        if (res && typeof res === "object") {
            const sqlQuery = res.sqlQuery || null;
            const messageId = res.messageId ?? res.id ?? null;
            if (Object.prototype.hasOwnProperty.call(res, "aiResponse")) {
                const aiResponse = res.aiResponse;
                const messageText = typeof aiResponse === "object"
                    ? JSON.stringify(aiResponse)
                    : String(aiResponse ?? "");
                return { messageText, sqlQuery, messageId };
            }
            return { messageText: JSON.stringify(res), sqlQuery, messageId };
        }

        return { messageText: String(res || ""), sqlQuery: null, messageId: null };
    };

    const getFilenameFromHeader = (contentDisposition) => {
        if (!contentDisposition) return null;
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utf8Match && utf8Match[1]) {
            return decodeURIComponent(utf8Match[1]);
        }
        const asciiMatch = contentDisposition.match(/filename="?([^\";]+)"?/i);
        return asciiMatch && asciiMatch[1] ? asciiMatch[1] : null;
    };

    const handleExportCsv = async (messageId) => {
        if (readOnly) return;
        if (!messageId || !currentConversationId) return;
        setExportingMessageId(messageId);
        try {
            const token = await getToken();
            const response = await ChatService.exportConversationCsv(currentConversationId, messageId, token);
            const contentType = response.headers?.['content-type'] || 'text/csv';
            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            const filename = getFilenameFromHeader(response.headers?.['content-disposition'])
                || `conversation-${currentConversationId}-message-${messageId}.csv`;
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setExportingMessageId(null);
        }
    };

    const handleSendMessage = async (e) => {
        if (readOnly) { e?.preventDefault?.(); return; }
        e.preventDefault();
        if (!userMessage.trim()) return;

        if (!currentConversationId) {
            setShowNewConversationModal(true);
            setPendingMessage(userMessage);
            setUserMessage('');
            return;
        }

        const newMsgs = [
            ...messages,
            { sender: 'User', messageText: userMessage },
            { sender: 'System', messageText: '<span class="spinner-border spinner-border-sm"></span> Thinking...' },
        ];
        setMessages(newMsgs);
        setUserMessage('');
        scrollToBottom();
        setLoading(true);

        try {
            const token = await getToken();
            const res = await ChatService.sendMessage(currentConversationId, userMessage, token, menu);
            const { messageText, sqlQuery, messageId } = normalizeAiResponse(res);
            setMessages([
                ...messages,
                { sender: 'User', messageText: userMessage },
                { sender: 'AI', messageText, sqlQuery, messageId }
            ]);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
            scrollToBottom();
        }
    };

    const handleConfirmDelete = async (conversationId) => {
        if (readOnly) return;
        const token = await getToken();
        await ChatService.deleteConversation(conversationId, token);
        setConversations(conversations.filter((c) => c.conversationId !== conversationId));
        if (currentConversationId === conversationId) {
            setCurrentConversationId(null);
            setMessages([]);
        }
        setShowDeleteModal(false);
    };

    const handleRenameConversation = async (description) => {
        if (readOnly) return;
        const token = await getToken();
        await ChatService.renameConversation(conversationToRename.conversationId, description, token);
        setConversations(
            conversations.map((c) =>
                c.conversationId === conversationToRename.conversationId ? { ...c, description } : c
            )
        );
        setShowRenameModal(false);
        setConversationToRename(null);
    };

    // const renderMessage = (msg, i) => {
    //     const html = parseMarkdownToHtml(msg.messageText);
    //     return (
    //         <div
    //             key={i}
    //             className={`message ${msg.sender === 'User' ? 'user' : 'ai'}`}
    //             dangerouslySetInnerHTML={{ __html: html }}
    //         />
    //     );
    // };

    const renderMessage = (msg, i) => {
        let parsed = null;
        const messageText = typeof msg.messageText === "string"
            ? msg.messageText
            : String(msg.messageText || "");

        // Try parsing the message JSON
        try {
            parsed = JSON.parse(messageText);
        } catch (e) {
            parsed = null;
        }

        console.log('Parsed message:', parsed);

        const parsedPayload = parsed?.aiResponse && typeof parsed.aiResponse === "object"
            ? parsed.aiResponse
            : parsed;
        const sqlQuery = msg.sender === "AI"
            ? (msg.sqlQuery || parsed?.sqlQuery || parsedPayload?.sqlQuery)
            : null;
        const messageId = msg.messageId || msg.id || parsed?.messageId || parsed?.id || null;
        const fallbackText = parsed?.aiResponse !== undefined && typeof parsed.aiResponse !== "object"
            ? String(parsed.aiResponse ?? "")
            : messageText;

        // If structured JSON with chartSpec & data
        if (parsedPayload && (parsedPayload.chartSpec || parsedPayload.data)) {
            const { insights, chartSpec, tableHtml, data } = parsedPayload;
            const htmlInsights = parseMarkdownToHtml(insights);

            return (
                <div key={i} className={`message ${msg.sender === "User" ? "user" : "ai"}`}>
                    {insights && (
                        <div className="insights-section mb-3">
                            <h5 className="fw-semibold mb-2">Insights</h5>
                            <p className="text-secondary" dangerouslySetInnerHTML={{ __html: htmlInsights }}></p>
                        </div>
                    )}

                    {chartSpec && data && data.length > 0 && (
                        <div className="chart-section mb-3 p-4">
                            <DynamicChart spec={chartSpec} data={data} />
                        </div>
                    )}

                    {/* {sqlQuery && (
                        <div className="sql-query-box">
                            <div className="sql-query-label">SQL</div>
                            <pre>{sqlQuery}</pre>
                        </div>
                    )} */}
                    {sqlQuery && (
                        <Accordion className="mb-2" alwaysOpen={false}>
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>View Query</Accordion.Header>
                                <Accordion.Body className='p-0'>
                                    <div className="sql-query-box">
                                        <div className="sql-query-label">SQL</div>
                                        <pre>{sqlQuery}</pre>
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    )}

                    {/* {tableHtml && (
                        <div
                            className="table-section"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(tableHtml),
                            }}
                        />
                    )} */}

                    {tableHtml && (
                        <Accordion className="" alwaysOpen={false}>
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>Data Preview</Accordion.Header>
                                <Accordion.Body>
                                    <div
                                        className='table-section table-responsive'
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(tableHtml),
                                        }}
                                    />
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    )}

                    {msg.sender === "AI" && messageId && (
                        <div className="message-actions">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleExportCsv(messageId)}
                                disabled={readOnly || (exportingMessageId === messageId)}
                            >
                                {exportingMessageId === messageId ? 'Exporting...' : 'Export CSV'}
                            </Button>
                        </div>
                    )}
                </div>
            );
        }

        // Fallback: regular text or markdown
        return (
            <div key={i} className={`message ${msg.sender === "User" ? "user" : "ai"}`}>
                {sqlQuery && (
                    <div className="sql-query-box">
                        <div className="sql-query-label">SQL</div>
                        <pre>{sqlQuery}</pre>
                    </div>
                )}
                <div
                    className="message-content"
                    dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(fallbackText),
                    }}
                />
                {msg.sender === "AI" && messageId && (
                    <div className="message-actions">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleExportCsv(messageId)}
                            disabled={readOnly || (exportingMessageId === messageId)}
                        >
                            {exportingMessageId === messageId ? 'Exporting...' : 'Export CSV'}
                        </Button>
                    </div>
                )}
            </div>
        );
    };



    return (
        <div className="chat-page">
            <div className={`conversation-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                <div className="sidebar-controls d-flex flex-column gap-2">
                    <Button
                        variant="light"
                        className="sidebar-button w-100"
                        onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                    >
                        <FontAwesomeIcon icon={faBars} />
                        {!isSidebarCollapsed && <span>Toggle Sidebar</span>}
                    </Button>
                    <Button disabled={readOnly} variant='light' size='md' className='sidebar-button w-100' onClick={() => setShowNewConversationModal(true)}>
                        <FontAwesomeIcon icon={faPlus} />
                        {!isSidebarCollapsed && <span>New Conversation</span>}
                    </Button>
                    <Form.Control
                        type="text"
                        placeholder="Search..."
                        className={`sidebar-search ${isSidebarCollapsed ? 'd-none' : ''}`}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size='sm'
                    />
                </div>

                <div className="conversations-list">
                    {!isSidebarCollapsed && (
                        filteredConversations && filteredConversations.map((conv) => (
                            <div
                                key={conv.conversationId}
                                className={`conversation-item ${currentConversationId === conv.conversationId ? 'active' : ''}`}
                                onClick={() => {
                                    setCurrentConversationId(conv.conversationId);
                                    fetchConversationHistory(conv.conversationId);
                                }}
                            >
                                <span>{conv.description || `Conversation ${conv.conversationId}`}</span>

                                <div className="conversation-item-actions">
                                    <button disabled={readOnly}
                                        className="btn btn-xs btn-outline-dark ms-1"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setConversationToRename(conv);
                                            setShowRenameModal(true);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faEdit} size="sm" />
                                    </button>
                                    <button disabled={readOnly}
                                        className="btn btn-xs btn-outline-danger ms-1"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setConversationToDelete(conv);
                                            setShowDeleteModal(true);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTrash} size="sm" />
                                    </button>
                                </div>

                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className="chat-area">
                <div className="messages">
                    {messages.length > 0 ? messages.map(renderMessage) : (
                        <div className="welcome-message h-100 w-100 d-flex flex-column align-items-center justify-content-center">
                            <h2>Welcome {instance.getActiveAccount().name}</h2>
                            <h2 className="fine-header">Do you need help?</h2>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                <form className="chat-input" onSubmit={handleSendMessage}>
                    <input
                        type="text"
                        value={userMessage}
                        onChange={(e) => setUserMessage(e.target.value)}
                        placeholder="Ask for help"
                        className="form-control"
                        disabled={readOnly || (loading)}
                    />
                    <button type="submit" disabled={readOnly || (loading || !userMessage.trim())} className="btn btn-secondary rounded-button ms-3 send-button">
                        <FontAwesomeIcon icon={faArrowUp} />
                    </button>
                </form>
            </div>

            <InputModal
                show={showNewConversationModal}
                onHide={() => { setShowNewConversationModal(false); setPendingMessage(''); }}
                title="New Conversation"
                inputLabel="Enter a name or description for the new conversation"
                onAcceptClick={handleCreateNewConversation}
                onCancelClick={() => setShowNewConversationModal(false)}
            />

            <InputModal
                show={showRenameModal}
                onHide={() => setShowRenameModal(false)}
                title="Rename Conversation"
                inputLabel="Enter a new name or description"
                onAcceptClick={handleRenameConversation}
                onCancelClick={() => setShowRenameModal(false)}
            />

            <ConfirmationModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                title="Delete Conversation"
                content={`Are you sure you want to delete "${conversationToDelete?.description || 'Unnamed'}"?`}
                onAcceptClick={() => handleConfirmDelete(conversationToDelete?.conversationId)}
                onCancelClick={() => setShowDeleteModal(false)}
            />
        </div>
    );
};

const ChatPage = ({ menu, title }) => (
    <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
        <PageLayout2 title={title}>
            <ChatPageContent menu={menu} />
        </PageLayout2>
    </MsalAuthenticationTemplate>
);

export default ChatPage;
